import { Drawer, DrawerProps } from 'antd';
import SafeImage from 'components/SafeImage';
import { memo, ReactNode, useContext, useEffect, useState } from 'react';
import { useTranslate } from '@tolgee/react';
import dayjs from 'dayjs';
import { cx, css } from '@emotion/css';
import { Virtuoso } from 'react-virtuoso';
import {
  FilterBarValueContext,
  FilterBarValueProvider,
} from '@knowledge/knowledges/singleClass/context';
import { searchInItemScope } from '@knowledge/common/helpers/search';
import FilterBar from '@knowledge/knowledges/singleClass/FilterBar';
import { CollapseOperation } from '@knowledge/knowledges/singleClassNew/CollapseOperation';
import { useUpdateEffect } from 'ahooks';
import { useStoreInfo } from '@knowledge/common/components/ItemSelectV2/useStoreInfo';
import { DownOutlined, LinkOutlined, UpOutlined } from '@ant-design/icons';
import styled from '@emotion/styled';
import { get } from 'lodash-es';
import { countSku } from '@knowledge/common/helpers/sku';
import Empty from 'components/Empty';

export interface PreviewItemProps extends DrawerProps {
  items: ItemSelectV2.Item[];
  footer?: ReactNode;
  updateTime?: number;
  storeScopesLimit?: General.StoreScope[];
}
const PreviewItem = memo((props: PreviewItemProps) => {
  const { t } = useTranslate();
  const { className, footer, items, updateTime, storeScopesLimit, ...restProps } = props;
  const [filterSpus, setFilterSpus] = useState<ItemSelectV2.Item[]>(items || []);
  const { storeScopes, keyword, keywordType } = useContext(FilterBarValueContext);
  useEffect(() => {
    if (items && Array.isArray(items)) {
      searchInItemScope(items, {
        storeScopes,
        keyword,
        keywordType,
        name: keywordType === 1 ? keyword : '',
      }).then((res: any) => {
        setFilterSpus(res);
      });
    }
  }, [items, storeScopes, keyword, keywordType]);
  const [foldState, setFoldState] = useState<'fold' | 'unfold'>('fold');
  return (
    <Drawer
      className={cx(
        css`
          .ant-drawer-body {
            padding: 0px;
          }
        `,
        className,
      )}
      title={
        <div>
          {t('查看商品')}
          <span className="text-[12px] text-[#84868c] ml-[8px]">
            SPU({get(items, 'length', 0)})&nbsp; SKU({countSku(items || [], 'skuInfo')})&nbsp;&nbsp;
            {t('aide.更新时间')}
            {dayjs(new Date().getTime()).format('YYYY-MM-DD HH:mm:ss')}
          </span>
        </div>
      }
      width={632}
      visible={true}
      destroyOnClose={true}
      {...restProps}
    >
      <div className="h-full flex flex-col overflow-hidden">
        <div className="flex-grow flex flex-col overflow-hidden px-[16px] pt-[12px] bg-[#f8f9fa]">
          <div className="flex-shrink-0 flex items-center justify-between pb-[12px]">
            <FilterBar
              className={css`
                & > div {
                  &:nth-child(2) {
                    width: 220px !important;
                  }
                }
              `}
              storeSelectPlacement="bottomRight"
              storeScopes={storeScopesLimit}
            />
            <CollapseOperation status={foldState} setStatus={setFoldState} />
          </div>
          <div className="flex-grow">
            {filterSpus.length === 0 ? (
              <div className="h-full flex items-center justify-center">
                <Empty />
              </div>
            ) : (
              <Virtuoso
                data={filterSpus}
                itemContent={(_, item) => {
                  return <ItemRender item={item} foldState={foldState} />;
                }}
              />
            )}
          </div>
        </div>
        {footer && (
          <div
            className={cx(
              'flex items-center justify-center h-[56px] flex-shrink-0',
              css`
                box-shadow: 0 -2px 8px 0 #a7b2cc1a, inset 0 1px 0 0 #f0f2f5;
              `,
            )}
          >
            {footer}
          </div>
        )}
      </div>
    </Drawer>
  );
});

PreviewItem.displayName = 'ItemGroupPreviewItem';

const PreviewItemWrapper = memo((props: PreviewItemProps) => {
  return (
    <FilterBarValueProvider>
      <PreviewItem {...props} />
    </FilterBarValueProvider>
  );
});

PreviewItemWrapper.displayName = 'ItemGroupPreviewItemWrapper';

export const ItemRender = memo(
  (props: { item: ItemSelectV2.Item; foldState?: 'fold' | 'unfold' }) => {
    const { item, foldState = 'fold' } = props;
    const [open, setOpen] = useState(foldState);
    useUpdateEffect(() => {
      setOpen(foldState);
    }, [foldState]);
    const {
      itemName,
      imageUrl: [imageUrl] = [],
      externalItemId: spuId,
      skuInfo = [],
      itemUrl,
      itemSku,
    } = item;
    const storeStr = useStoreInfo(item);
    const { t } = useTranslate();
    return (
      <div className="border border-solid border-[rgba(211,215,224,1)] box-border rounded-[2px] px-[12px] pt-[10px] pb-[8px] bg-[#fff] mb-[12px]">
        <div className="h-[80px] flex items-center">
          <div className="basis-auto overflow-hidden flex flex-grow">
            <div className="flex-shrink-0 !mr-[8px]">
              <Image hasSmallPic="both" src={imageUrl} width={80} height={80} />
            </div>
            <div className="basis-auto flex-grow overflow-hidden">
              <div className="flex items-start">
                <div
                  title={itemName}
                  className="w-full group truncate break-all cursor-pointer h-[40px] leading-[20px] hover:text-[#3366ff]"
                  onClick={(e) => {
                    e.stopPropagation();
                    window.open(itemUrl, '_blank');
                  }}
                >
                  <LinkOutlined className="!hidden float-right bg-[#d6e8ff] !text-[#336fff] h-[20px] w-[20px] !leading-[20px] group-hover:!block" />
                  {itemName}
                </div>
              </div>
              <div className="h-[36px] mt-[4px] leading-[18px] text-[12px] text-[#84868c]">
                <div className="truncate">
                  {t('marketing.店铺')}: {storeStr}
                </div>
                {!!skuInfo?.length && (
                  <div className="flex justify-between items-center">
                    <div className="flex items-center truncate basis-auto overflow-hidden mr-[8px]">
                      <span>ID: {spuId}</span>
                      <div className="mx-[4px]">|</div>
                      <span className="truncate">
                        {t('aide.主商品货号')}: {itemSku || '-'}
                      </span>
                    </div>
                    <div
                      onClick={() => {
                        setOpen(open === 'fold' ? 'unfold' : 'fold');
                      }}
                      className="flex-shrink-0 cursor-pointer hover:text-[#3366ff]"
                    >
                      {t('knowledge.共{{x}}个SKU', { x: skuInfo?.length })}
                      {open === 'fold' ? (
                        <DownOutlined className="ml-[4px]" />
                      ) : (
                        <UpOutlined className="ml-[4px]" />
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
        {open === 'unfold' && skuInfo.length > 0 && (
          <div className="pl-[22px] mt-[8px]">
            <div className="border-0 border-t border-solid border-[#f0f2f5] flex flex-wrap overflow-hidden">
              {skuInfo.map((sku) => {
                return (
                  <div
                    key={sku.skuId}
                    className="basis-1/2 flex-shrink-0 flex-grow-0 mt-[9px] pr-[8px] overflow-hidden"
                  >
                    <div
                      title={`${sku.sellerSku}(${sku.skuName})`}
                      className="w-full truncate break-all"
                    >
                      {sku.sellerSku}({sku.skuName})
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    );
  },
);

export const Image = styled(SafeImage)`
  width: 80px;
  height: 80px;
`;

export default PreviewItemWrapper;
